import ArrowBack from '@mui/icons-material/ArrowBack';
import ContentCopy from '@mui/icons-material/ContentCopy';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import MailOutline from '@mui/icons-material/MailOutline';
import ManageSearchOutlined from '@mui/icons-material/ManageSearchOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PeopleOutline from '@mui/icons-material/PeopleOutline';
import PersonOutline from '@mui/icons-material/PersonOutline';
import TodayOutlined from '@mui/icons-material/TodayOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import WatchLaterOutlined from '@mui/icons-material/WatchLaterOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { ReactNode } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import strings from '../../../common/Translation/Translate';
import axiosInstance from '../../../utils/axios';
import { privileges } from '../../../utils/constants/privileges';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import Currency from '../../../utils/hooks/getCurrency';
import { formatGuestCountCompact } from '../../../utils/validators/HelperFunctions';
import { User } from '../../../utils/redux/reducer/authentication-slice';
import { setCloneFromRFQ } from '../../../utils/redux/reducer/booking-slice';
import { useBookingDetailActions } from './BookingDetailPageActions';
import type { LineItemPaymentSummary } from './BookingDetailTabs/BookingBillingPaymentPanels';
import AddLineItem from './BookingDetailButtons/AddLineItem';
import { CancelButton } from './BookingDetailButtons/CancelButton';
import { ChangeToTentativeButtonButton } from './BookingDetailButtons/ChangeToTentativeButton';
import { ConfirmBookingButton } from './BookingDetailButtons/ConfirmBookingButton';
import { DeleteButton } from './BookingDetailButtons/DeleteButton';
import { MoveBookingButton } from './BookingDetailButtons/MoveBookingButton';
import { EditButton } from './BookingDetailButtons/EditButton';
import { bookingDetailToolbarBackButtonSx, bookingDetailToolbarButtonSx } from './bookingDetailToolbarSx';

dayjs.extend(advancedFormat);

const getStatusChipStyles = (s: string) => {
  switch (s?.toUpperCase()) {
    case 'CONFIRMED':
      return { backgroundColor: '#E6F4EA', color: '#188038' };
    case 'TENTATIVE':
      return { backgroundColor: '#FFF4E5', color: '#D17A00' };
    case 'WAITLISTED':
      return { backgroundColor: '#E8F0FE', color: '#1967D2' };
    case 'AWAITING_CONFIRMATION':
      return { backgroundColor: '#A3A329', color: '#fff' };
    case 'CANCELLED':
      return { backgroundColor: '#FDECEA', color: '#D93025' };
    case 'ACTUALIZED':
      return { backgroundColor: '#800080', color: '#fff' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#616161' };
  }
};

const ACTUALIZATION_FINALIZED_CHIP_STYLES = { backgroundColor: '#800080', color: '#fff' };

const paymentAlertAmountSx = { fontWeight: 800, fontVariantNumeric: 'tabular-nums' as const };

/** Replace `{amount}` / `{paid}` / `{date}` in copy; amount/paid spans render bold in the alert. */
function interpolatePaymentAlertLine(
  template: string,
  amountFormatted: string,
  paidFormatted?: string,
  dateFormatted?: string,
): ReactNode {
  const parts = template.split(/(\{amount\}|\{paid\}|\{date\})/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part === '{amount}') {
          return (
            <Box component="span" key={`a-${i}`} sx={paymentAlertAmountSx}>
              {amountFormatted}
            </Box>
          );
        }
        if (part === '{paid}') {
          return (
            <Box component="span" key={`p-${i}`} sx={paymentAlertAmountSx}>
              {paidFormatted ?? ''}
            </Box>
          );
        }
        if (part === '{date}') {
          return (
            <Box component="span" key={`d-${i}`} sx={{ fontWeight: 700 }}>
              {dateFormatted ?? ''}
            </Box>
          );
        }
        return part;
      })}
    </>
  );
}

const summaryBoxSx = {
  neutral: {
    border: 1,
    borderColor: 'divider',
    bgcolor: '#fafafa',
  },
  paid: {
    border: 1,
    borderColor: '#B7DFC4',
    bgcolor: '#E6F4EA',
  },
  due: {
    border: 1,
    borderColor: '#F5C6C0',
    bgcolor: '#FDECEA',
  },
  status: {
    border: 1,
    borderColor: 'divider',
    bgcolor: '#fafafa',
  },
} as const;

function PaymentSummaryBox({
  label,
  value,
  variant,
  footer,
}: {
  label: string;
  value: ReactNode;
  variant: keyof typeof summaryBoxSx;
  footer?: ReactNode;
}) {
  const amountColor = variant === 'paid' ? '#137333' : variant === 'due' ? '#C5221F' : 'text.primary';
  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.25,
        borderRadius: 1.5,
        flex: '1 1 0',
        minWidth: { xs: '45%', sm: 120 },
        maxWidth: { sm: 1 },
        ...summaryBoxSx[variant],
      }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, display: 'block', mb: 0.35, letterSpacing: 0.04, fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: amountColor,
          lineHeight: 1.25,
          fontSize: '1rem',
        }}>
        {value}
      </Typography>
      {footer}
    </Box>
  );
}

function formatTimePart(raw?: string | null): string {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';

  if (/^\d{1,2}:\d{2}(:\d{2})?/.test(s) && !s.includes('T') && s.length <= 14) {
    const strict = dayjs(s, ['HH:mm:ss.SSS', 'HH:mm:ss', 'HH:mm', 'H:mm'], true);
    if (strict.isValid()) return strict.format('HH:mm');
    const loose = dayjs(s, ['HH:mm:ss', 'HH:mm', 'H:mm']);
    if (loose.isValid()) return loose.format('HH:mm');
  }

  const d = dayjs(s);
  if (d.isValid()) return d.format('HH:mm');

  const m = s.match(/\b(\d{1,2}:\d{2}(?::\d{2})?)\b/);
  if (m) return formatTimePart(m[1]);

  return s;
}

function formatBookingTimeRange(li: any): string {
  const start = formatTimePart(li?.startTime);
  const end = formatTimePart(li?.endTime);
  if (start && end) return `${start} - ${end}`;
  if (start) return start;
  if (end) return end;

  const slot = li?.timeSlotName;
  if (slot != null && String(slot).trim()) return String(slot).trim();

  const sdRaw = li?.startDatetime;
  if (sdRaw != null && sdRaw !== '') {
    const sd = dayjs.unix(parseInt(String(sdRaw), 10) / 1000);
    if (sd.isValid()) {
      const startStr = sd.format('HH:mm');
      const edRaw = li?.endDatetime;
      if (edRaw != null && edRaw !== '') {
        const ed = dayjs.unix(parseInt(String(edRaw), 10) / 1000);
        if (ed.isValid()) return `${startStr} - ${ed.format('HH:mm')}`;
      }
      const endFromField = formatTimePart(li?.endTime);
      if (endFromField) return `${startStr} - ${endFromField}`;
      return startStr;
    }
  }

  return '—';
}

function MetaItem({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={0.75}
      sx={{ minWidth: 0, maxWidth: '100%', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
      <Box sx={{ color: 'text.secondary', mt: 0.15, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Typography
        variant="body2"
        color="text.primary"
        sx={{ fontWeight: 500, lineHeight: 1.35, wordBreak: 'break-word', minWidth: 0 }}>
        {children}
      </Typography>
    </Stack>
  );
}

type BookingDetailStickyHeaderProps = {
  bookingLineItem: any;
  paymentSummary: LineItemPaymentSummary | null;
};

export const BookingDetailStickyHeader: React.FC<BookingDetailStickyHeaderProps> = ({
  bookingLineItem,
  paymentSummary,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useSelector(User) as { role?: { value?: string } } | null;
  const isBbrAdmin = user?.role?.value === 'ROLE_BBR_ADMIN';
  const {
    futureDateForCancel,
    futureDateForBeo,
    onSuccess,
    dispatch,
    Navigate,
    IsPrivilege,
    getBeoButtonText,
    isBeoButtonDisabled,
  } = useBookingDetailActions();

  const line = bookingLineItem;
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [bulkConvertDialogOpen, setBulkConvertDialogOpen] = useState(false);
  const [bulkConvertLoading, setBulkConvertLoading] = useState(false);
  /** Sync guard so a second Confirm click in the same tick cannot start another POST. */
  const savingRef = useRef(false);

  const bookingId = line?.booking?.id;
  const actualizationFinalized = Boolean(line?.actualizationFinalized);

  // Check if there are eligible line items for bulk convert
  const relatedBookings = line?.relatedBookings || [];
  const eligibleLineItems = useMemo(() => {
    return relatedBookings.filter(
      (item: any) => item.status === 'AWAITING_CONFIRMATION' || item.status === 'TENTATIVE',
    );
  }, [relatedBookings]);

  const showBulkConvert = eligibleLineItems.length > 1;

  const handleBulkConvertClick = () => {
    setMoreAnchor(null);
    setBulkConvertDialogOpen(true);
  };

  const handleBulkConvertConfirm = async () => {
    if (!bookingId) return;
    if (savingRef.current || bulkConvertLoading) return;

    savingRef.current = true;
    try {
      setBulkConvertLoading(true);
      await axiosInstance.post(URLS.bulkConvertToConfirmed(bookingId));
      setBulkConvertDialogOpen(false);
      onSuccess();
    } catch (error: any) {
      console.error('Bulk convert failed:', error);
    } finally {
      savingRef.current = false;
      setBulkConvertLoading(false);
    }
  };

  const {
    showConfirm,
    showTentative,
    showAwaitingConfirmation,
    showCancel,
    showEdit,
    showClone,
    showAddLine,
    showBeo,
    showDelete,
    showMove,
  } = useMemo(() => {
    const li = line;
    if (!li?.id) {
      return {
        showConfirm: false,
        showTentative: false,
        showAwaitingConfirmation: false,
        showCancel: false,
        showEdit: false,
        showClone: Boolean(IsPrivilege?.includes(privileges.cloneBooking)),
        showAddLine: false,
        showBeo: false,
        showDelete: false,
        showMove: false,
      };
    }
    const showConfirm =
      (li.status === 'WAITLISTED' || li.status === 'TENTATIVE' || li.status === 'AWAITING_CONFIRMATION') &&
      futureDateForCancel;
    const showTentative = (li.status === 'ENQUIRY' || li.status === 'WAITLISTED') && futureDateForCancel;
    const showAwaitingConfirmation = li.status === 'TENTATIVE' && futureDateForCancel;
    const showCancel =
      li.status !== 'CANCELLED' &&
      li.status !== 'CANCELLATION_REQUEST' &&
      (li.status === 'ENQUIRY' || li.status === 'AWAITING_CONFIRMATION' || futureDateForCancel) &&
      IsPrivilege.includes(privileges.cancelBooking);
    const showEdit =
      li.status !== 'CANCELLED' &&
      li.status !== 'CANCELLATION_REQUEST' &&
      futureDateForCancel &&
      IsPrivilege.includes(privileges.editBooking);
    const showClone = IsPrivilege?.includes(privileges.cloneBooking);
    const showAddLine =
      IsPrivilege?.includes(privileges.editBooking) &&
      li.status !== 'CANCELLED' &&
      li.status !== 'EXPIRED' &&
      (li.status === 'ENQUIRY' || futureDateForCancel);
    const showBeo = IsPrivilege?.includes(privileges.view_beo) && li.status === 'CONFIRMED';
    const showDelete = IsPrivilege?.includes(privileges.deleteBooking) && !li.deleted;
    const showMove =
      IsPrivilege?.includes(privileges.editBooking) &&
      !li.deleted &&
      li.status !== 'CANCELLED' &&
      li.status !== 'CANCELLATION_REQUEST' &&
      futureDateForCancel;
    return {
      showConfirm,
      showTentative,
      showAwaitingConfirmation,
      showCancel,
      showEdit,
      showClone,
      showAddLine,
      showBeo,
      showDelete,
      showMove,
    };
  }, [line, futureDateForCancel, IsPrivilege]);

  const status = String(line?.status ?? '');
  const hasActualization = Boolean(line?.hasActualization);

  /** DB status stays CONFIRMED after capture; align chip with calendar (ACTUALIZED when revenue captured). */
  const displayStatus = useMemo(() => {
    const raw = status.toUpperCase();
    if (hasActualization && raw === 'CONFIRMED') {
      return 'ACTUALIZED';
    }
    return status;
  }, [status, hasActualization]);

  const statusChipLabel = actualizationFinalized
    ? strings.bookingDetail_status_actualization_finalized
    : displayStatus
    ? displayStatus.toUpperCase()
    : '—';
  const statusChipStyles = actualizationFinalized
    ? ACTUALIZATION_FINALIZED_CHIP_STYLES
    : getStatusChipStyles(displayStatus);

  const isComplementaryBooking = Boolean(
    line?.isComplementary ?? line?.complementary ?? line?.booking?.isComplementary ?? line?.booking?.complementary,
  );

  const lineItemPaymentFallback = line?.id
    ? {
        total: hasActualization
          ? line.actualTotalCost ?? line.totalCost
          : line.totalAmount ?? line.total ?? line.totalCost,
        paid: line.amountPaid ?? line.paid,
        due: line.amountDue,
      }
    : { total: undefined, paid: undefined, due: undefined };

  const ref = line?.booking?.bookingReference ?? '—';
  const venue = line?.venueName ?? '—';
  const eventDate = line?.startDate ? dayjs(line.startDate).format('D MMM YYYY') : '—';
  const times = formatBookingTimeRange(line);
  const customer = line?.booking?.customerName ?? '—';
  const exp = line?.expectedGuests ?? 0;
  const guar = line?.guaranteedGuests ?? 0;
  const guestSummary = formatGuestCountCompact(guar, exp);

  const money = (v: number | null | undefined) => (v === null || v === undefined ? '—' : Currency(v));

  const displayTotal = paymentSummary?.total ?? lineItemPaymentFallback.total;
  const displayPaid = paymentSummary?.paid ?? lineItemPaymentFallback.paid;
  const displayDue = paymentSummary?.due ?? lineItemPaymentFallback.due;

  const dueNum = typeof displayDue === 'number' && !Number.isNaN(displayDue) ? displayDue : Number(displayDue);
  const paidNum = typeof displayPaid === 'number' && !Number.isNaN(displayPaid) ? displayPaid : Number(displayPaid);
  const totalNum =
    typeof displayTotal === 'number' && !Number.isNaN(displayTotal) ? displayTotal : Number(displayTotal);

  const paymentStatusChip = useMemo(() => {
    if (isComplementaryBooking) {
      return { label: strings.bookingDetail_badge_complementary, sx: { bgcolor: '#e0f7fa', color: '#00838f' } };
    }
    if (totalNum == null || Number.isNaN(totalNum) || totalNum <= 0) {
      return { label: strings.bookingDetail_paymentStatus_pending, sx: { bgcolor: '#FFF4E5', color: '#D17A00' } };
    }
    if (!Number.isNaN(dueNum) && dueNum <= 0) {
      return { label: strings.bookingDetail_paymentStatus_paidFull, sx: { bgcolor: '#E6F4EA', color: '#188038' } };
    }
    if (!Number.isNaN(paidNum) && paidNum > 0 && dueNum > 0) {
      return { label: strings.bookingDetail_paymentStatus_partial, sx: { bgcolor: '#FFF4E5', color: '#E37400' } };
    }
    return { label: strings.bookingDetail_paymentStatus_pending, sx: { bgcolor: '#FFF4E5', color: '#D17A00' } };
  }, [isComplementaryBooking, totalNum, paidNum, dueNum]);

  const customerEmail = line?.booking?.email ?? '';

  const paymentAlert = useMemo(() => {
    if (!line?.id || line.deleted) return null;

    const statusUpper = displayStatus.toUpperCase();

    if (statusUpper === 'AWAITING_CONFIRMATION') {
      return {
        severity: 'info' as const,
        icon: <InfoOutlined fontSize="small" />,
        body: strings.bookingDetail_statusInfo_awaitingConfirmation,
        canSendReminder: false,
        reminderAmountText: null,
      };
    }
    const eps = 0.009;
    const paidSafe = Number.isNaN(paidNum) || paidNum < 0 ? 0 : paidNum;
    const totalOk = !Number.isNaN(totalNum) && totalNum > eps;
    const balance = totalOk ? Math.max(0, totalNum - paidSafe) : 0;
    const dueOk = !Number.isNaN(dueNum) && dueNum > eps;
    const dueCredit = !Number.isNaN(dueNum) && dueNum < -eps;
    const cancelledLike = statusUpper === 'CANCELLED' || statusUpper === 'CANCELLATION_REQUEST';
    const softStatuses = ['WAITLISTED', 'TENTATIVE', 'ENQUIRY', 'AWAITING_CONFIRMATION'];
    const isSoft = softStatuses.includes(statusUpper);

    const now = dayjs();
    let isEventPast = false;
    if (line?.endDate) {
      isEventPast = now.isAfter(dayjs(line.endDate).endOf('day'));
    } else if (line?.startDatetime != null && String(line.startDatetime).trim() !== '') {
      const ms = parseInt(String(line.startDatetime), 10);
      if (!Number.isNaN(ms)) isEventPast = now.isAfter(dayjs.unix(ms / 1000));
    } else if (line?.startDate) {
      isEventPast = now.isAfter(dayjs(line.startDate).endOf('day'));
    }

    let amountNumeric: number | null = null;
    let show = false;

    if (cancelledLike) {
      show = dueOk || dueCredit;
      if (show) amountNumeric = dueNum;
    } else if (isSoft) {
      if (dueOk) amountNumeric = dueNum;
      else if (balance > eps) amountNumeric = balance;
      else if (totalOk) amountNumeric = totalNum;
      show = amountNumeric != null && amountNumeric > eps;
    } else {
      if (dueOk) amountNumeric = dueNum;
      else if (balance > eps) amountNumeric = balance;
      show = amountNumeric != null && amountNumeric > eps;
    }

    if (!show || amountNumeric == null) return null;

    const dueF = money(amountNumeric);
    const paidF = money(displayPaid);

    let severity: 'warning' | 'info' | 'error' = 'warning';
    let icon: ReactNode = <WarningAmberOutlined fontSize="small" />;
    let template: string;

    if (statusUpper === 'ENQUIRY') {
      severity = 'info';
      icon = <InfoOutlined fontSize="small" />;
      template = isEventPast
        ? strings.bookingDetail_paymentAlert_enquiry_past
        : strings.bookingDetail_paymentAlert_enquiry;
    } else if (statusUpper === 'WAITLISTED') {
      template = isEventPast
        ? strings.bookingDetail_paymentAlert_waitlisted_past
        : strings.bookingDetail_paymentAlert_waitlisted;
    } else if (statusUpper === 'TENTATIVE') {
      const bookingDate = line?.booking?.bookingDate;
      const holdDays = line?.booking?.holdDays;
      const hasReleaseDate = Boolean(bookingDate && holdDays && Number(holdDays) > 0);
      if (hasReleaseDate) {
        const releaseDate = dayjs(bookingDate).add(Number(holdDays), 'day');
        const isPastRelease = releaseDate.isBefore(dayjs(), 'day');
        template = isPastRelease
          ? strings.bookingDetail_paymentAlert_tentative_past_release
          : strings.bookingDetail_paymentAlert_tentative_with_release;
      } else {
        template = isEventPast
          ? strings.bookingDetail_paymentAlert_tentative_past
          : strings.bookingDetail_paymentAlert_tentative;
      }
    } else if (statusUpper === 'ACTUALIZED') {
      severity = 'info';
      icon = <InfoOutlined fontSize="small" />;
      template = strings.bookingDetail_paymentAlert_actualized;
    } else if (statusUpper === 'CANCELLED') {
      severity = 'error';
      icon = <ErrorOutline fontSize="small" />;
      template = strings.bookingDetail_paymentAlert_cancelled;
    } else if (statusUpper === 'CANCELLATION_REQUEST') {
      template = strings.bookingDetail_paymentAlert_cancellationRequested;
    } else if (statusUpper === 'CONFIRMED') {
      const hasTotal = totalOk;
      const paidOk = !Number.isNaN(paidNum) && paidNum > eps;
      const shareDue = hasTotal && totalNum > eps && amountNumeric != null ? amountNumeric / totalNum : 1;
      const nearlySettled = paidOk && shareDue <= 0.15 && amountNumeric != null && amountNumeric > eps;

      if (hasTotal && amountNumeric != null && amountNumeric > eps && !paidOk) {
        template = strings.bookingDetail_paymentAlert_confirmed_noPaid;
      } else if (hasTotal && nearlySettled) {
        template = strings.bookingDetail_paymentAlert_confirmed_nearly;
      } else if (hasTotal && paidOk && amountNumeric != null && amountNumeric > eps && !nearlySettled) {
        template = strings.bookingDetail_paymentAlert_confirmed_partial;
      } else {
        template = strings.bookingDetail_paymentAlert_confirmed;
      }

      if (isEventPast) {
        if (template === strings.bookingDetail_paymentAlert_confirmed_noPaid) {
          template = strings.bookingDetail_paymentAlert_confirmed_noPaid_past;
        } else if (template === strings.bookingDetail_paymentAlert_confirmed_nearly) {
          template = strings.bookingDetail_paymentAlert_confirmed_nearly_past;
        } else if (template === strings.bookingDetail_paymentAlert_confirmed_partial) {
          template = strings.bookingDetail_paymentAlert_confirmed_partial_past;
        } else if (template === strings.bookingDetail_paymentAlert_confirmed) {
          template = strings.bookingDetail_paymentAlert_confirmed_past;
        }
      }
    } else {
      template = strings.bookingDetail_paymentAlert_default;
    }

    const releaseDateFormatted =
      statusUpper === 'TENTATIVE' && line?.booking?.bookingDate && line?.booking?.holdDays
        ? dayjs(line.booking.bookingDate).add(Number(line.booking.holdDays), 'day').format('D MMM YYYY')
        : undefined;

    const partialTemplates = new Set([
      strings.bookingDetail_paymentAlert_confirmed_partial,
      strings.bookingDetail_paymentAlert_confirmed_partial_past,
    ]);
    const body = partialTemplates.has(template)
      ? interpolatePaymentAlertLine(template, dueF, paidF, releaseDateFormatted)
      : interpolatePaymentAlertLine(template, dueF, undefined, releaseDateFormatted);

    const amountForReminder = !cancelledLike && amountNumeric != null && amountNumeric > eps ? amountNumeric : null;
    const canSendReminder =
      Boolean(customerEmail) &&
      amountForReminder != null &&
      !cancelledLike &&
      !['ENQUIRY', 'WAITLISTED', 'AWAITING_CONFIRMATION'].includes(statusUpper);

    return {
      severity,
      icon,
      body,
      canSendReminder,
      reminderAmountText: canSendReminder ? money(amountForReminder) : null,
      includesHoldRelease: Boolean(
        statusUpper === 'TENTATIVE' &&
          (template === strings.bookingDetail_paymentAlert_tentative_with_release ||
            template === strings.bookingDetail_paymentAlert_tentative_past_release),
      ),
    };
  }, [
    line?.id,
    line?.deleted,
    displayStatus,
    line?.startDate,
    line?.startDatetime,
    line?.endDate,
    line?.booking?.bookingDate,
    line?.booking?.holdDays,
    dueNum,
    paidNum,
    totalNum,
    displayPaid,
    customerEmail,
  ]);

  const handleSendPaymentReminder = () => {
    if (!customerEmail || !ref || !paymentAlert?.canSendReminder) return;
    const amt = paymentAlert.reminderAmountText ?? money(displayDue);
    const subject = encodeURIComponent(`${strings.bookingDetail_paymentReminderSubject} ${ref}`);
    const body = encodeURIComponent(
      strings.formatString(strings.bookingDetail_paymentReminderBody, amt, ref) as string,
    );
    window.open(`mailto:${customerEmail}?subject=${subject}&body=${body}`, '_blank');
  };

  // Hold-days alert only when payment alert did not already include the release date.
  const holdDaysAlert = useMemo(() => {
    if (!line?.id || line.deleted) return null;
    if (paymentAlert?.includesHoldRelease) return null;

    const statusUpper = displayStatus.toUpperCase();
    if (statusUpper !== 'TENTATIVE') return null;

    const bookingDate = line?.booking?.bookingDate;
    const holdDays = line?.booking?.holdDays;

    if (!bookingDate || !holdDays || holdDays === 0) return null;

    const releaseDate = dayjs(bookingDate).add(Number(holdDays), 'day');
    const releaseDateFormatted = releaseDate.format('D MMM YYYY');
    const isPastRelease = releaseDate.isBefore(dayjs(), 'day');

    const message = (
      isPastRelease ? strings.bookingDetail_holdDaysAlert_past : strings.bookingDetail_holdDaysAlert_future
    ).replace('{date}', releaseDateFormatted);

    return {
      severity: (isPastRelease ? 'warning' : 'info') as 'warning' | 'info',
      icon: isPastRelease ? <WarningAmberOutlined fontSize="small" /> : <InfoOutlined fontSize="small" />,
      body: message,
    };
  }, [
    line?.id,
    line.deleted,
    displayStatus,
    line?.booking?.bookingDate,
    line?.booking?.holdDays,
    paymentAlert?.includesHoldRelease,
  ]);

  if (!line?.id) {
    return null;
  }

  if (line.deleted) {
    return (
      <Paper
        component="header"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
        }}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<ArrowBack />}
          onClick={() => navigate(routes.bookings)}
          sx={{ ...bookingDetailToolbarBackButtonSx }}>
          {strings.bookingDetails_backToDiary}
        </Button>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, fontFamily: 'monospace' }}>
          {ref}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      component="header"
      elevation={0}
      sx={{
        position: 'sticky',
        top: { xs: 56, sm: 64 },
        zIndex: theme.zIndex.appBar - 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        px: { xs: 1.5, sm: 2.5 },
        pt: 1.5,
        pb: 2,
        mb: 0,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}>
      <Button
        size="small"
        variant="text"
        color="primary"
        startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
        onClick={() => navigate(routes.bookings)}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          mb: 1,
          px: 0.5,
          minWidth: 0,
          '&:hover': { bgcolor: 'action.hover' },
        }}>
        {strings.bookingDetails_backToDiary}
      </Button>

      <Box sx={{ minWidth: 0, mb: 1.5, width: '100%', maxWidth: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            columnGap: 2,
            rowGap: 1,
            mb: 1.5,
            width: '100%',
          }}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ flex: '0 1 auto', minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                fontFamily: 'ui-monospace, monospace',
                letterSpacing: -0.02,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                wordBreak: 'break-word',
              }}>
              {ref}
            </Typography>
            <Chip
              label={statusChipLabel}
              size="small"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.04,
                height: 26,
                ...statusChipStyles,
              }}
            />
          </Stack>

          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            gap={1}
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            alignItems="center"
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 auto' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: 0, md: 'max-content' },
              maxWidth: '100%',
              ml: { xs: 0, md: 'auto' },
              '& .MuiButton-root': {
                maxWidth: { xs: '100%', md: 'none' },
              },
            }}>
            {showConfirm && (
              <ConfirmBookingButton
                id={line.id}
                onSuccess={onSuccess}
                bookingEmail={line.booking?.email}
                compact
                isContractTemplatePresent={line.booking?.isContractTemplatePresent}
                isProposalTemplatePresent={line.booking?.isProposalTemplatePresent}
                isPiTemplatePresent={line.booking?.isPiTemplatePresent}
                isEnquiryTemplatePresent={line.booking?.isEnquiryTemplatePresent}
              />
            )}
            {showTentative && (
              <ChangeToTentativeButtonButton
                id={line.id}
                action="toTentative"
                onSuccess={onSuccess}
                bookingEmail={line.booking?.email}
                compact
                isContractTemplatePresent={line.booking?.isContractTemplatePresent}
                isProposalTemplatePresent={line.booking?.isProposalTemplatePresent}
                isPiTemplatePresent={line.booking?.isPiTemplatePresent}
                isEnquiryTemplatePresent={line.booking?.isEnquiryTemplatePresent}
                isEnquiryStatus={line.status === 'ENQUIRY'}
              />
            )}
            {showAwaitingConfirmation && (
              <ChangeToTentativeButtonButton
                id={line.id}
                action="toAwaitingConfirmation"
                onSuccess={onSuccess}
                bookingEmail={line.booking?.email}
                compact
                isContractTemplatePresent={line.booking?.isContractTemplatePresent}
                isProposalTemplatePresent={line.booking?.isProposalTemplatePresent}
                isPiTemplatePresent={line.booking?.isPiTemplatePresent}
                isEnquiryTemplatePresent={line.booking?.isEnquiryTemplatePresent}
              />
            )}
            {showEdit && <EditButton id={line.id} compact />}
            {showClone && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<ContentCopy sx={{ fontSize: 18 }} />}
                onClick={() => {
                  dispatch(setCloneFromRFQ(false));
                  Navigate(`${routes.addBooking}/${line.booking.id}`);
                }}
                sx={{ ...bookingDetailToolbarButtonSx, borderColor: 'divider' }}>
                {strings.BookingDetailButonTextClone}
              </Button>
            )}
            {showAddLine && (
              <AddLineItem
                dates={{
                  startDate: dayjs(line?.startDate),
                  endDate: dayjs(line?.endDate),
                }}
                eventType={line.booking?.eventType?.id}
                id={line?.id}
                bookingId={line.booking?.id}
                onSuccess={onSuccess}
                bookingEmail={line.booking?.email}
                pageHeader={{
                  customerName: line?.booking?.customerName || '',
                  eventTypeLabel: line?.booking?.eventType?.label || line?.booking?.eventType?.name || '',
                }}
                isContractTemplatePresent={line.booking?.isContractTemplatePresent}
                isProposalTemplatePresent={line.booking?.isProposalTemplatePresent}
                isPiTemplatePresent={line.booking?.isPiTemplatePresent}
                isEnquiryTemplatePresent={line.booking?.isEnquiryTemplatePresent}
              />
            )}
            {showBeo && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={isBeoButtonDisabled(futureDateForBeo, line)}
                onClick={() => Navigate(`${routes.addBeo}/${line.id}`)}
                sx={{ ...bookingDetailToolbarButtonSx, borderColor: 'divider' }}>
                {getBeoButtonText(futureDateForBeo, line)}
              </Button>
            )}
            {(showCancel || showDelete || showMove || isBbrAdmin || showBulkConvert) && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  endIcon={<ExpandMore />}
                  onClick={e => setMoreAnchor(e.currentTarget)}
                  sx={{ ...bookingDetailToolbarButtonSx, borderColor: 'divider', px: 1.25 }}>
                  <MoreHoriz sx={{ fontSize: 20 }} />
                </Button>
                <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)} keepMounted>
                  {showCancel && (
                    <CancelButton
                      compact
                      onTriggerClick={() => setMoreAnchor(null)}
                      id={line.id}
                      onSuccess={onSuccess}
                    />
                  )}
                  {showDelete && <DeleteButton compact onTriggerClick={() => setMoreAnchor(null)} id={line.id} />}
                  {showBulkConvert && (
                    <Tooltip
                      title={strings.convertAllToConfirmedTooltip}
                      placement="left"
                      arrow
                      enterDelay={400}
                      disableInteractive>
                      <Box component="span" sx={{ display: 'block' }}>
                        <MenuItem
                          sx={{ py: 0.75, gap: 1, fontSize: '0.875rem', fontWeight: 500 }}
                          onClick={handleBulkConvertClick}>
                          <CheckCircleOutline sx={{ fontSize: 18, color: 'text.secondary' }} />
                          {strings.convertAllToConfirmed}
                        </MenuItem>
                      </Box>
                    </Tooltip>
                  )}
                  {showMove && (
                    <MoveBookingButton
                      compact
                      id={line.id}
                      currentStartDate={line.startDate}
                      onSuccess={onSuccess}
                      onTriggerClick={() => setMoreAnchor(null)}
                    />
                  )}
                  {isBbrAdmin && (showCancel || showDelete || showMove || showBulkConvert) && (
                    <Divider sx={{ my: 0.5 }} />
                  )}
                  {isBbrAdmin && (
                    <MenuItem
                      sx={{ py: 0.75, gap: 1, fontSize: '0.875rem', fontWeight: 500 }}
                      onClick={() => {
                        setMoreAnchor(null);
                        navigate(
                          `${routes.saBookingLookup}?bookingReference=${line?.booking?.bookingReference || line?.id}`,
                        );
                      }}>
                      <ManageSearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                      {strings.sa_booking_lookup_title}
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Stack>
        </Box>

        <Stack spacing={1} sx={{ maxWidth: { xs: '100%', md: 900 }, width: '100%' }}>
          <Stack direction="row" rowGap={1} columnGap={2.5} flexWrap="wrap" alignItems="flex-start">
            <MetaItem icon={<LocationOnOutlined sx={{ fontSize: 18 }} />}>{venue}</MetaItem>
            <MetaItem icon={<TodayOutlined sx={{ fontSize: 18 }} />}>{eventDate}</MetaItem>
            <MetaItem icon={<WatchLaterOutlined sx={{ fontSize: 18 }} />}>{times}</MetaItem>
          </Stack>
          <Stack direction="row" rowGap={1} columnGap={2.5} flexWrap="wrap" alignItems="flex-start">
            <MetaItem icon={<PersonOutline sx={{ fontSize: 18 }} />}>
              {strings.bookingDetail_header_customerPrefix} {customer}
            </MetaItem>
            <MetaItem icon={<PeopleOutline sx={{ fontSize: 18 }} />}>{guestSummary}</MetaItem>
          </Stack>
        </Stack>
      </Box>

      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        gap={1.25}
        sx={{
          mb: paymentAlert || holdDaysAlert ? 1.5 : 0,
          alignItems: 'stretch',
          width: '100%',
          maxWidth: '100%',
        }}>
        <PaymentSummaryBox
          label={strings.bookingDetail_summary_total.toUpperCase()}
          value={money(displayTotal)}
          variant="neutral"
        />
        <PaymentSummaryBox
          label={strings.bookingDetail_summary_paid.toUpperCase()}
          value={money(displayPaid)}
          variant="paid"
        />
        <PaymentSummaryBox
          label={strings.bookingDetail_summary_due.toUpperCase()}
          value={money(displayDue)}
          variant="due"
        />
        <PaymentSummaryBox
          label={strings.bookingDetail_paymentStatus_label.toUpperCase()}
          value={
            <Chip
              label={paymentStatusChip.label}
              size="small"
              sx={{ mt: 0.5, fontWeight: 700, height: 24, ...paymentStatusChip.sx }}
            />
          }
          variant="status"
        />
      </Stack>

      {paymentAlert && (
        <Alert
          severity={paymentAlert.severity}
          icon={paymentAlert.icon}
          sx={{
            alignItems: 'center',
            py: 1,
            px: 1.5,
            ...(paymentAlert.severity === 'warning'
              ? {
                  bgcolor: '#FFF8E1',
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: '#FFE082',
                }
              : {}),
            '& .MuiAlert-message': { width: '100%' },
          }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
            justifyContent="space-between">
            <Typography component="div" variant="body2" sx={{ fontWeight: 500, lineHeight: 1.45 }}>
              {paymentAlert.body}
            </Typography>
            {paymentAlert.canSendReminder ? (
              <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={handleSendPaymentReminder}
                  sx={{ textTransform: 'none', fontWeight: 600, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  {strings.bookingDetail_sendPaymentReminder}
                </Button>
                <IconButton
                  size="small"
                  onClick={handleSendPaymentReminder}
                  sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
                  aria-label={strings.bookingDetail_sendPaymentReminder}>
                  <MailOutline fontSize="small" />
                </IconButton>
              </Stack>
            ) : null}
          </Stack>
        </Alert>
      )}

      {holdDaysAlert && (
        <Alert
          severity={holdDaysAlert.severity}
          icon={holdDaysAlert.icon}
          sx={{
            alignItems: 'center',
            py: 1,
            px: 1.5,
            mt: 1.5,
            ...(holdDaysAlert.severity === 'warning'
              ? {
                  bgcolor: '#FFF8E1',
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: '#FFE082',
                }
              : {}),
            '& .MuiAlert-message': { width: '100%' },
          }}>
          <Typography component="div" variant="body2" sx={{ fontWeight: 500, lineHeight: 1.45 }}>
            {holdDaysAlert.body}
          </Typography>
        </Alert>
      )}

      {/* Bulk Convert Confirmation Dialog */}
      <Dialog
        open={bulkConvertDialogOpen}
        onClose={() => !bulkConvertLoading && setBulkConvertDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{strings.convertAllToConfirmed}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {strings.convertAllToConfirmedConfirmation}
            <br />
            <br />
            <strong>
              {eligibleLineItems.length} {strings.lineItems_text}
            </strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConvertDialogOpen(false)} disabled={bulkConvertLoading} color="inherit">
            {strings.cancelText}
          </Button>
          <Button onClick={handleBulkConvertConfirm} disabled={bulkConvertLoading} variant="contained" color="primary">
            {bulkConvertLoading ? 'Loading' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
